"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@workspace/ui/components/button";
import { Input } from "@workspace/ui/components/input";
import { cn } from "@workspace/ui/lib/utils";
import {
  Send,
  Sparkles,
  FileText,
  Search,
  Pencil,
  Expand,
  SpellCheck,
  Loader2,
  Copy,
  Check,
} from "lucide-react";
import { NetworkError } from "@workspace/network";
import { apiClient } from "@/lib/api-client";

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
}

interface AiPanelProps {
  documentId: string;
  getEditorContent?: () => string;
}

const QUICK_ACTIONS = [
  { action: "summarize", label: "Summarize", icon: FileText },
  { action: "research", label: "Research", icon: Search },
  { action: "rewrite", label: "Rewrite", icon: Pencil },
  { action: "expand", label: "Expand", icon: Expand },
  { action: "fix_grammar", label: "Fix Grammar", icon: SpellCheck },
];

export function AiPanel({  getEditorContent }: AiPanelProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom
  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages]);

  // `useCallback` so the slash/bubble-menu listener effect below can depend on
  // this honestly instead of re-subscribing on every render.
  const executeAction = useCallback(async (
    action: string,
    customPrompt?: string,
    content?: string
  ) => {
    setLoading(true);

    const userMessage: Message = {
      id: crypto.randomUUID(),
      role: "user",
      content:
        customPrompt ||
        `${action.replace("_", " ")} the document`,
    };
    setMessages((prev) => [...prev, userMessage]);

    const docContent = content || getEditorContent?.() || "";

    try {
      const { response } = await apiClient.post(
        "/api/ai",
        {
          action,
          content: docContent,
          prompt: customPrompt,
          context: docContent.slice(0, 2000),
        },
        { responseType: "stream" },
      );

      // Read SSE stream
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let assistantContent = "";
      const assistantId = crypto.randomUUID();

      setMessages((prev) => [
        ...prev,
        { id: assistantId, role: "assistant", content: "" },
      ]);

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          // Parse SSE data lines
          const lines = chunk.split("\n");
          for (const line of lines) {
            if (line.startsWith("data: ")) {
              const data = line.slice(6);
              if (data === "[DONE]") continue;
              try {
                const parsed = JSON.parse(data);
                const delta = parsed.choices?.[0]?.delta?.content;
                if (delta) {
                  assistantContent += delta;
                  setMessages((prev) =>
                    prev.map((m) =>
                      m.id === assistantId
                        ? { ...m, content: assistantContent }
                        : m
                    )
                  );
                }
              } catch {
                // Non-JSON chunk, append as raw text
                assistantContent += data;
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === assistantId
                      ? { ...m, content: assistantContent }
                      : m
                  )
                );
              }
            }
          }
        }
      }
    } catch (error) {
      const errorMsg =
        error instanceof NetworkError && error.status
          ? `AI request failed: ${error.status}`
          : error instanceof Error
            ? error.message
            : "Something went wrong";
      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          role: "assistant",
          content: `Error: ${errorMsg}`,
        },
      ]);
    } finally {
      setLoading(false);
    }
  }, [getEditorContent]);

  // Listen for AI commands from slash menu or bubble menu. Declared after
  // `executeAction` so it can name it as a dependency.
  useEffect(() => {
    const handler = (e: CustomEvent<{ action: string }>) => {
      executeAction(e.detail.action);
    };
    window.addEventListener("docs:ai-command", handler as EventListener);
    return () =>
      window.removeEventListener("docs:ai-command", handler as EventListener);
  }, [executeAction]);

  const handleSend = () => {
    if (!input.trim()) return;
    const prompt = input.trim();
    setInput("");
    executeAction("custom", prompt);
  };

  const copyMessage = (id: string, content: string) => {
    navigator.clipboard.writeText(content);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Quick actions */}
      <div className="p-3 border-b flex flex-wrap gap-1.5 shrink-0">
        {QUICK_ACTIONS.map(({ action, label, icon: Icon }) => (
          <Button
            key={action}
            variant="outline"
            size="sm"
            className="h-8 text-sm gap-1.5"
            onClick={() => executeAction(action)}
            disabled={loading}
          >
            <Icon className="h-3.5 w-3.5" />
            {label}
          </Button>
        ))}
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-3 space-y-3">
        {messages.length === 0 && (
          <div className="text-center text-sm text-muted-foreground py-8">
            <Sparkles className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>Ask the AI assistant anything</p>
            <p className="text-sm mt-1">
              Summarize, research, rewrite, or ask questions
            </p>
          </div>
        )}

        {messages.map((msg) => (
          <div
            key={msg.id}
            className={cn(
              "text-sm rounded-lg px-3 py-2",
              msg.role === "user"
                ? "bg-primary text-primary-foreground ml-8"
                : "bg-muted mr-4"
            )}
          >
            <div className="whitespace-pre-wrap break-words">
              {msg.content || (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
            </div>
            {msg.role === "assistant" && msg.content && (
              <div className="mt-1.5 flex justify-end">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => copyMessage(msg.id, msg.content)}
                >
                  {copiedId === msg.id ? (
                    <Check className="h-3.5 w-3.5" />
                  ) : (
                    <Copy className="h-3.5 w-3.5" />
                  )}
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Input */}
      <div className="p-3 border-t shrink-0">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSend();
          }}
          className="flex gap-2"
        >
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask anything..."
            className="text-sm"
            disabled={loading}
          />
          <Button
            type="submit"
            size="icon"
            className="shrink-0"
            disabled={loading || !input.trim()}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
